// frontend/src/contexts/HistoryContext.js
import React, { createContext, useState, useEffect, useCallback, useContext, useMemo } from 'react';
import { AuthContext } from './AuthContext'; // Dependency on AuthContext
import translationEn from '../i18n/supported_languages/en/translation.json';

const HistoryContext = createContext();

const ALL_CONTENT_CATEGORIES = Object.keys(translationEn.contentMetadata.inclusion);

export const INITIAL_FILTERS = {
    contentTypes: ['text', 'image', 'audio', 'video'],
    scoreTypes: [],
    likelihoods: ['Yes', 'No'],
    contentCategories: ALL_CONTENT_CATEGORIES,
    safetyFlags: [], 
    filenameSearch: '',
    startDate: '',
    endDate: ''
};

const calculateLocalVetraScore = (score1, score2) => {
    if (score1 == null || score2 == null) return null;
    const p_1 = score1 / 100.0;
    const p_2 = score2 / 100.0;
    const prob_or = 1 - ((1 - p_1) * (1 - p_2));
    const synergy_bonus = Math.pow(p_1 * p_2, 0.5) * 0.15;
    return Math.floor(Math.min(prob_or + synergy_bonus, 1.0) * 100);
};

const HistoryProvider = ({ children }) => {
    const { isAuthenticated, user, fetchWithAuth } = useContext(AuthContext);

    const [historyData, setHistoryData] = useState([]);
    const [isHistoryLoading, setIsHistoryLoading] = useState(false);
    const [historyError, setHistoryError] = useState(null);
    const [hasFetchedHistory, setHasFetchedHistory] = useState(false);
    const [hasInitializedFilters, setHasInitializedFilters] = useState(false);

    // --- Dynamic Tasks State ---
    const [tasksConfig, setTasksConfig] = useState({});
    const [masterTaskOrder, setMasterTaskOrder] = useState([]);
    const [threatRadarLimit, setThreatRadarLimit] = useState(100);

    const [historyFilters, setHistoryFilters] = useState(INITIAL_FILTERS);
    const [filteredHistoryData, setFilteredHistoryData] = useState([]);

    // --- Fetch Tasks Config for Dynamic Filtering ---
    useEffect(() => {
        const loadTasksConfig = async () => {
            try {
                const response = await fetch('/api/config/tasks');
                if (response.ok) {
                    const data = await response.json();
                    if (data && data.tasks) {
                        setTasksConfig(data.tasks);
                        setMasterTaskOrder(Object.keys(data.tasks));
                        // Set the limit if provided, default to 100
                        if (data.threat_radar_limit) {
                            setThreatRadarLimit(data.threat_radar_limit);
                        }
                    }
                }
            } catch (err) {
                console.error("Failed to load tasks config for History Context:", err);
            }
        };
        loadTasksConfig();
    }, []);

    // --- 1. DYNAMICALLY CALCULATE AVAILABLE TASKS BASED ON ACTUAL DATA ---
    const availableFilterTasks = useMemo(() => {
        if (!masterTaskOrder || masterTaskOrder.length === 0) return [];

        const defaultFallback = ['digital_integrity_score', 'ai_detection', 'fake_news_detection']
            .filter(t => masterTaskOrder.includes(t));

        // If no data, return the defaults
        if (!historyData || historyData.length === 0) {
            return defaultFallback;
        }

        const foundTasks = new Set();

        // Scan history to see what was actually run
        historyData.forEach(item => {
            const itemTasks = Object.keys(item.tasks || {});
            
            // Add base tasks
            itemTasks.forEach(t => foundTasks.add(t));

            // Check if any meta-tasks are applicable based on the base tasks present
            Object.entries(tasksConfig || {}).forEach(([metaKey, metaConfig]) => {
                if (metaConfig.is_meta_task) {
                    const deps = metaConfig.depends_on || [];
                    if (deps.length > 0 && deps.every(d => itemTasks.includes(d))) {
                        foundTasks.add(metaKey);
                    }
                }
            });
        });

        if (foundTasks.size === 0) return defaultFallback;

        // Return the found tasks, strictly sorted by the YAML master order
        return masterTaskOrder.filter(task => foundTasks.has(task));
    }, [historyData, tasksConfig, masterTaskOrder]);

    // --- 2. BUILD DYNAMIC INITIAL FILTERS ---
    const dynamicInitialFilters = useMemo(() => {
        const initial = { ...INITIAL_FILTERS, scoreTypes: availableFilterTasks };
        availableFilterTasks.forEach(taskKey => {
            initial[`score_${taskKey}`] = [0, 100];
        });
        return initial;
    }, [availableFilterTasks]);

    // --- 3. INITIALIZE FILTERS ONCE HISTORY LOADS ---
    useEffect(() => {
        // Once data is fetched, set the initial filters so all discovered checkboxes are ticked
        if (hasFetchedHistory && !hasInitializedFilters && availableFilterTasks.length > 0) {
            setHistoryFilters(dynamicInitialFilters);
            setHasInitializedFilters(true);
        }
    }, [hasFetchedHistory, hasInitializedFilters, availableFilterTasks, dynamicInitialFilters]);

    const fetchHistory = useCallback(async () => {
        if (hasFetchedHistory || isHistoryLoading) return;
        setIsHistoryLoading(true);
        setHistoryError(null);
        try {
            const response = await fetchWithAuth('/api/user/history');
            if (!response.ok) throw new Error(`Failed to fetch history. Status: ${response.status}`);
            const data = await response.json();
            setHistoryData(data);
            setHasFetchedHistory(true);
        } catch (err) {
            setHistoryError(err.message || 'An unknown error occurred.');
            setHistoryData([]);
        } finally {
            setIsHistoryLoading(false);
        }
    }, [hasFetchedHistory, isHistoryLoading, fetchWithAuth]);

    // Effect to reset history when user logs out
    useEffect(() => {
        if (!isAuthenticated) {
            setHistoryData([]);
            setFilteredHistoryData([]);
            setHasFetchedHistory(false);
            setHasInitializedFilters(false);
        }
    }, [isAuthenticated]);

    // Effect to trigger history fetch when user is authenticated
    useEffect(() => {
        if (isAuthenticated && !user?.is_guest && !hasFetchedHistory) {
            fetchHistory();
        }
    }, [isAuthenticated, user, hasFetchedHistory, fetchHistory]);

    // Effect to perform filtering whenever filters or raw data change
    useEffect(() => {
        // 1. Create a shallow copy
        let results = [...historyData];

        // 2. Apply Filters
        if (historyFilters.contentTypes.length === 0) { results = []; }
        else if (historyFilters.contentTypes.length < 4) { results = results.filter(item => historyFilters.contentTypes.includes(item.content_type)); }

        // --- Check against dynamic Meta-Tasks ---
        if (historyFilters.scoreTypes.length === 0) { results = []; }
        else if (historyFilters.scoreTypes.length < masterTaskOrder.length) { 
            results = results.filter(item => {
                const itemKeys = Object.keys(item.tasks || {});
                
                // Add meta-task keys if their base dependencies are met
                Object.entries(tasksConfig || {}).forEach(([metaKey, config]) => {
                    if (config.is_meta_task && config.depends_on && config.depends_on.every(d => itemKeys.includes(d))) {
                        itemKeys.push(metaKey);
                    }
                });
                
                return itemKeys.some(taskKey => historyFilters.scoreTypes.includes(taskKey));
            }); 
        }

        if (historyFilters.likelihoods.length < 2 && historyFilters.likelihoods.length > 0) {
            results = results.filter(item => {
                const tasks = Object.values(item.tasks || {});
                if (tasks.length === 0) return false;
                return tasks.every(task => historyFilters.likelihoods.includes(task.likelihood));
            });
        } else if (historyFilters.likelihoods.length === 0) {
            results = [];
        }

        // --- Calculate Meta-Scores for the Slider values ---
        results = results.filter(item => {
            return historyFilters.scoreTypes.every(scoreType => {
                let task = item.tasks[scoreType];
                
                // If the task is missing, check if it's a meta-task we can calculate
                if (!task && tasksConfig?.[scoreType]?.is_meta_task) {
                    const deps = tasksConfig[scoreType].depends_on || [];
                    if (deps.length === 2 && item.tasks[deps[0]] && item.tasks[deps[1]]) {
                        const metaScore = calculateLocalVetraScore(item.tasks[deps[0]].score, item.tasks[deps[1]].score);
                        task = { score: metaScore };
                    }
                }
                const scoreRange = historyFilters[`score_${scoreType}`];
                if (!task || !scoreRange) return true;
                return task.score >= scoreRange[0] && task.score <= scoreRange[1];
            });
        });

        // --- Filter by Content Category ---
        // Logic:
        // 1. If ALL categories are selected (default), show EVERYTHING (including legacy items with no metadata).
        // 2. If subset selected, show ONLY items that match the selection.
        const isAllCategoriesSelected = historyFilters.contentCategories.length === ALL_CONTENT_CATEGORIES.length;

        if (!isAllCategoriesSelected) {
            results = results.filter(item => {
                // ... get metadata ...
                const tasks = item.tasks || {};
                const metadata = Object.values(tasks).find(t => t?.content_metadata)?.content_metadata;

                // If filter is active, we exclude items without metadata
                if (!metadata || !metadata.category) return false;
                
                return historyFilters.contentCategories.includes(metadata.category);
            });
        }

        // --- Filter by Safety Flags ---
        // Logic: 
        // 1. If NO flags selected (default), show EVERYTHING (don't filter by risk).
        // 2. If SOME flags selected, show ONLY items that have AT LEAST ONE of those flags.
        if (historyFilters.safetyFlags.length > 0) {
            results = results.filter(item => {
                const tasks = item.tasks || {};
                const metadata = Object.values(tasks).find(t => t?.content_metadata)?.content_metadata;

                // If user wants to see specific risks, hide items with no metadata/flags
                if (!metadata || !metadata.safety_flags) return false;

                // Check if the item has ANY of the selected flags
                return metadata.safety_flags.some(flag => historyFilters.safetyFlags.includes(flag));
            });
        }

        if (historyFilters.filenameSearch) {
            const searchTerm = historyFilters.filenameSearch.toLowerCase();
            // Regex to identify and remove the hash (e.g., "-a1b2c3.jpg" -> ".jpg")
            // This MUST match the regex used in your filenameOptions generation logic
            const hashRegex = /-[a-f0-9]{6}(\.[a-z0-9]+)$/i;

            results = results.filter(item => {
                const rawFilename = item.source_filename || '';
                // Clean the raw filename from the data item
                const cleanFilename = rawFilename.replace(hashRegex, '$1');
                
                // Compare the clean search term against the clean filename
                return cleanFilename.toLowerCase().includes(searchTerm);
            });
        }

        const { startDate, endDate } = historyFilters;
        if (startDate) { const start = new Date(startDate); results = results.filter(item => new Date(item.latest_timestamp) >= start); }
        if (endDate) { const end = new Date(endDate); end.setHours(23, 59, 59, 999); results = results.filter(item => new Date(item.latest_timestamp) <= end); }

        // 3. Explicitly Sort by Date (Newest First)
        // This fixes the issue where items would stay in a weird order after clearing filters.
        results.sort((a, b) => new Date(b.latest_timestamp) - new Date(a.latest_timestamp));

        setFilteredHistoryData(results);
    }, [historyFilters, historyData, masterTaskOrder.length, tasksConfig]);

    const value = {
        historyData,
        filteredHistoryData,
        isHistoryLoading,
        historyError,
        fetchHistory,
        hasFetchedHistory,
        setHasFetchedHistory,
        historyFilters,
        setHistoryFilters,
        tasksConfig,
        masterTaskOrder,
        dynamicInitialFilters,
        availableFilterTasks,
        threatRadarLimit
    };

    return <HistoryContext.Provider value={value}>{children}</HistoryContext.Provider>;
};

export { HistoryContext, HistoryProvider };